import { Fragment } from "react";
import { Link } from "react-router-dom";
import { useLocale } from "@/locale/LocaleContext";
import styles from "./environment-save-studies.module.css";

export const STUDY_VARIANTS = ["letter", "rule", "index", "poster"] as const;
export type StudyId = (typeof STUDY_VARIANTS)[number];
type PhaseId = "running" | "saving" | "archived" | "restoring" | "attention";

const PHASES: readonly PhaseId[] = ["running", "saving", "archived", "restoring", "attention"];
const MOMENTS = ["2026-10-18 09:40 UTC", "2026-10-22 16:05 UTC"] as const;

export function EnvironmentSaveStudy({ variant, phase }: { variant: StudyId; phase: PhaseId }) {
  if (variant === "rule") return <Rule phase={phase} />;
  if (variant === "index") return <Index phase={phase} />;
  if (variant === "poster") return <Poster phase={phase} />;
  return <Letter phase={phase} />;
}

function Letter({ phase }: { phase: PhaseId }) {
  const { m } = useLocale();
  const copy = m.environmentSave;
  const study = copy.studies;
  const rows = [
    {
      act: copy.saveRelease,
      title: copy.phases[phase].name,
      body: `${copy.phases[phase].body} ${copy.macReady} ${copy.progressSteps}`,
      lead: true,
    },
    { act: copy.restore, title: study.letterScopeTitle, body: study.letterScope, lead: false },
    { act: copy.exportAction, title: study.letterRecordTitle, body: study.letterRecord, lead: false },
    { act: copy.deleteAction, title: study.letterTermTitle, body: study.letterTerm, lead: false },
  ];
  return (
    <article className={styles.letter} data-study="letter">
      <p className={styles.letterDate}>{study.letterDate}</p>
      {rows.map((row) => (
        <Fragment key={row.act}>
          <p className={styles.letterAct}>
            {row.act}
            <em>{study.notFiled}</em>
          </p>
          <section className={styles.letterSection} data-lead={row.lead ? "" : undefined}>
            {row.lead ? (
              <h1 className={styles.letterTitle}>{row.title}</h1>
            ) : (
              <h2 className={styles.letterTitle}>{row.title}</h2>
            )}
            <p>{row.body}</p>
          </section>
        </Fragment>
      ))}
      <Link className={styles.studyBack} to="/app/environment">
        {copy.back}
      </Link>
    </article>
  );
}

function Rule({ phase }: { phase: PhaseId }) {
  const { m } = useLocale();
  const copy = m.environmentSave;
  const study = copy.studies;
  return (
    <article className={styles.rule} data-study="rule">
      <h1 className={styles.ruleState}>{copy.phases[phase].name}</h1>
      <div className={styles.ruleMeasure}>
        <div className={styles.ruleTrack} />
        <ol className={styles.ruleTicks}>
          {PHASES.map((id) => (
            <li key={id} className={styles.ruleTick} data-current={id === phase ? "" : undefined}>
              <i />
              <span>{copy.phases[id].name}</span>
            </li>
          ))}
        </ol>
      </div>
      <div className={styles.ruleLines}>
        <p>{copy.phases[phase].body}</p>
        <p>{study.ruleHold}</p>
        <p>{copy.progressSteps}</p>
        <p className={styles.ruleClosed}>{study.ruleClosed}</p>
      </div>
      <Link className={styles.studyBack} to="/app/environment">
        {copy.back}
      </Link>
    </article>
  );
}

export type EnvironmentCabinetFacts = {
  liveCompute: boolean;
  storageProvisioning: boolean;
  paidCheckout: boolean;
  activeDemoLeases: number;
  openArchiveRequests: number;
};

export function EnvironmentIndexCabinet({ facts }: { facts: EnvironmentCabinetFacts }) {
  return <Index cabinet facts={facts} />;
}

function countLabel(template: string, count: number) {
  return template.replaceAll("{n}", String(count));
}

function Index({
  phase,
  cabinet = false,
  facts,
}: {
  phase?: PhaseId;
  cabinet?: boolean;
  facts?: EnvironmentCabinetFacts;
}) {
  const { m } = useLocale();
  const copy = m.environmentSave;
  const study = copy.studies;
  const phaseId = phase ?? "archived";
  const mac =
    !cabinet || !facts
      ? { key: study.indexMac, value: study.indexMacValue }
      : facts.liveCompute
        ? { key: study.indexMac, value: study.indexMacLive, gloss: study.indexMacLiveGloss }
        : facts.activeDemoLeases > 0
          ? {
              key: study.indexMac,
              value: countLabel(study.indexMacDemo, facts.activeDemoLeases),
              gloss: study.indexMacDemoGloss,
            }
          : { key: study.indexMac, value: study.indexMacValue };
  const groups: {
    title: string;
    note?: string;
    rows: { key: string; value: string; mark?: "state"; gloss?: string }[];
  }[] = [
    {
      title: study.indexNow,
      rows: [
        cabinet
          ? {
              key: m.nav.environment,
              value: study.indexState.running,
              mark: "state" as const,
              gloss: facts?.liveCompute ? study.indexStateOn : study.indexStateOff,
            }
          : {
              key: copy.phases[phaseId].name,
              value: study.indexState[phaseId],
              mark: "state" as const,
            },
        mac,
        { key: study.indexChecks, value: cabinet ? study.indexChecksIdle : study.indexChecksValue },
        cabinet
          ? { key: study.indexOrder, value: study.indexOrderValue, gloss: study.indexOrderGloss }
          : { key: study.indexOrder, value: study.indexOrderValue },
        ...(cabinet
          ? [
              {
                key: study.indexArchiveRequests,
                value:
                  facts && facts.openArchiveRequests > 0
                    ? countLabel(study.indexOpenCount, facts.openArchiveRequests)
                    : study.indexNone,
                gloss: study.indexArchiveGloss,
              },
            ]
          : []),
      ],
    },
    {
      title: study.indexMoments,
      rows: [
        ...(cabinet
          ? []
          : [
              { key: MOMENTS[0], value: study.indexSample },
              { key: MOMENTS[1], value: study.indexSample },
            ]),
        { key: copy.lastSaveTitle, value: study.indexNone },
        { key: copy.sizeTitle, value: copy.sizeUnknown },
        { key: copy.compatibilityTitle, value: study.indexNotChecked },
      ],
    },
    {
      title: study.indexScope,
      rows: [
        { key: copy.included, value: study.indexIncludedValue },
        { key: copy.excluded, value: study.indexExcludedValue },
        { key: copy.cloudOnly, value: study.indexCloudValue },
        { key: copy.nonRestorable, value: study.indexLostValue },
      ],
    },
    {
      title: study.indexTerm,
      rows: [
        { key: copy.retentionTitle, value: study.indexMonths },
        ...(cabinet
          ? [{ key: study.indexExpiry, value: study.indexExpiryValue, gloss: study.indexExpiryGloss }]
          : []),
        { key: study.indexPrice, value: study.indexPriceValue },
      ],
    },
    {
      title: study.indexClosed,
      note: study.indexClosedNote,
      rows: [
        { key: copy.quotaTitle, value: study.indexQuotaValue },
        cabinet && facts?.paidCheckout
          ? { key: study.indexPayment, value: study.indexPayFlag, gloss: study.indexPayFlagGloss }
          : { key: study.indexPayment, value: study.indexPayValue },
        { key: copy.icloudTitle, value: study.indexNotSignedIn },
        cabinet && facts?.storageProvisioning
          ? { key: copy.saveRelease, value: study.indexSaveValue, gloss: study.indexSaveReported }
          : { key: copy.saveRelease, value: study.indexSaveValue },
        { key: copy.restore, value: study.indexRestoreValue },
        { key: copy.exportAction, value: study.indexExportValue },
        { key: copy.deleteAction, value: study.indexDeleteValue, gloss: copy.deleteConsequence },
        { key: study.indexImage, value: study.indexNotPromised },
      ],
    },
  ];
  return (
    <div className={styles.index} data-study="index" data-cabinet={cabinet ? "" : undefined}>
      {groups.map((group) => (
        <section key={group.title} className={styles.indexSection}>
          <h2 className={styles.indexHead}>{group.title}</h2>
          {group.note ? <p className={styles.indexHeadnote}>{group.note}</p> : null}
          {group.rows.map((row) => (
            <div key={row.key}>
              <div className={styles.indexRow} data-mark={row.mark}>
                <span className={styles.indexKey}>{row.key}</span>
                <span className={styles.indexDots} />
                <span className={styles.indexVal}>{row.value}</span>
              </div>
              {row.gloss ? <p className={styles.indexGloss}>{row.gloss}</p> : null}
            </div>
          ))}
        </section>
      ))}
      {cabinet ? null : (
        <Link className={styles.studyBack} to="/app/environment">
          {copy.back}
        </Link>
      )}
    </div>
  );
}

function Poster({ phase }: { phase: PhaseId }) {
  const { m } = useLocale();
  const copy = m.environmentSave;
  const study = copy.studies;
  const acts = [copy.saveRelease, copy.restore, copy.exportAction, copy.deleteAction];
  const notes = [
    { key: MOMENTS[0], value: study.posterSample },
    { key: MOMENTS[1], value: study.posterSample },
    { key: copy.retentionTitle, value: study.posterTerm },
    { key: study.indexOrder, value: study.indexOrderValue },
    { key: study.posterClosed, value: study.posterVoid },
  ];
  return (
    <article className={styles.poster} data-study="poster">
      <h1 className={styles.posterWord}>{copy.phases[phase].name}</h1>
      <p className={styles.posterCaption}>{copy.phases[phase].body}</p>
      <p className={styles.posterScope}>{study.posterScope}</p>
      <div className={styles.posterActs}>
        {acts.map((act) => (
          <p key={act}>
            <span className={styles.posterAct}>{act}</span>
            <span className={styles.posterVoid}>{study.posterVoid}</span>
          </p>
        ))}
      </div>
      <ul className={styles.posterNotes}>
        {notes.map((note) => (
          <li key={note.key}>
            <span>{note.key}</span>
            <span>{note.value}</span>
          </li>
        ))}
      </ul>
      <Link className={styles.studyBack} to="/app/environment">
        {copy.back}
      </Link>
    </article>
  );
}
